/**
 * Lazy loading utility with automatic retry for chunk loading failures
 * Fixes production issues where initial chunk loads fail but work after refresh
 */

import { ComponentType, lazy } from 'react';

interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
}

/**
 * Wraps React.lazy with automatic retry logic for chunk loading failures
 * @param importFn - The dynamic import function
 * @param options - Retry configuration
 * @returns A lazy-loaded component that retries on failure
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): React.LazyExoticComponent<T> {
  const { maxRetries = 3, retryDelay = 1000 } = options;

  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      let attempts = 0;

      const attemptLoad = () => {
        attempts++;

        importFn()
          .then(resolve)
          .catch((error) => {
            console.error(`Chunk load attempt ${attempts} failed:`, error);

            // Check if it's a chunk loading error
            const isChunkLoadError =
              error?.name === 'ChunkLoadError' ||
              error?.message?.includes('Failed to fetch dynamically imported module') ||
              error?.message?.includes('Loading chunk') ||
              error?.message?.includes('Importing a module script failed');

            if (isChunkLoadError && attempts < maxRetries) {
              console.log(`Retrying chunk load... (attempt ${attempts + 1}/${maxRetries})`);

              // Exponential backoff
              const delay = retryDelay * Math.pow(2, attempts - 1);

              setTimeout(() => {
                attemptLoad();
              }, delay);
            } else if (attempts >= maxRetries) {
              console.error(`Failed to load chunk after ${maxRetries} attempts. Reloading page...`);

              // Last resort: reload the page
              // This ensures the user gets the latest version of the app
              if (typeof window !== 'undefined') {
                window.location.reload();
              } else {
                reject(error);
              }
            } else {
              reject(error);
            }
          });
      };

      attemptLoad();
    });
  });
}
